import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  linkedSignal,
  OnInit,
  signal,
} from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { Currency } from "@front/m19-api-client";
import {
  ACOS,
  COST,
  DAILY_BUDGET,
  Metric,
  MIN_DAILY_SPEND,
  MONTHLY_BUDGET,
  TACOS,
  TARGET_ACOS,
  TARGET_TACOS,
} from "@front/m19-metrics";
import { CurrencyStat } from "@front/m19-models";
import { DataSetEventAnnotation } from "@front/m19-models/dataset";
import { AdStatsType, MetricsSelectorKey, UserSelectionService } from "@front/m19-services";
import {
  ChartConfig,
  IChartComponent,
  IDateAggreationSwitchComponent,
  IToggleComponent,
  Option,
  SpinnerComponent,
} from "@front/m19-ui";
import { DateAggregation } from "@front/m19-utils";
import { TranslocoDirective } from "@jsverse/transloco";
import { ActivityEventFiltersComponent } from "@m19-board/activity-event-filters.component";
import { DateRangeSelectorComponent } from "@m19-board/date-range-selector/date-range-selector.component";
import { MetricSelectorComponent } from "@m19-board/insights/metric-selector/metric-selector.component";
import { map, Observable, startWith, tap } from "rxjs";
import {
  ActivityEventAnnotationsService,
  ActivityEventType,
} from "../../activities/activity-event-annotations.service";
import { ChartData, DataSetConfiguration } from "./chart-modal.component";

const STRATEGY_CONFIG_METRICS: Metric<CurrencyStat>[] = [
  TARGET_ACOS,
  TARGET_TACOS,
  MIN_DAILY_SPEND,
  DAILY_BUDGET,
  MONTHLY_BUDGET,
];

interface ChartDataTyped<T extends CurrencyStat> extends ChartData<T> {
  acosTarget?: boolean;
  tacosTarget?: boolean;
  monthlyBudget?: boolean;
  dailyBudget?: boolean;
  minDailySpend?: boolean;
}

@Component({
  selector: "app-chart-renderer",
  template: ` <div *transloco="let t" class="flex h-full flex-col">
    @if (loading()) {
      <div class="flex w-full items-center justify-center">
        <ISpinner [display]="true"></ISpinner>
      </div>
    } @else {
      <div class="mb-2 flex flex-row justify-end">
        @if (withRangeSelector()) {
          <app-date-range-selector />
        }
      </div>
      <div>
        <!-- TODO: review the displayMode toggle inputs in metric-selector -->
        <app-metric-selector
          [data]="(totalData() | async)!"
          [previousPeriodData]="(totalPreviousData() | async)!"
          [availableMetrics]="metrics()"
          (selectedMetricsChange)="selectMetrics($event)"
          [localStorageKey]="localStorageKey()"
          [splitData]="splitData()"
        ></app-metric-selector>
      </div>
      <div class="my-3 flex flex-row justify-between">
        <div class="flex flex-auto items-center gap-2">
          @if (annotations$()) {
            <div class="flex flex-auto items-center">
              <div class="mr-4 flex items-center gap-2">
                <IToggle
                  [checked]="displayEventAnnotation()"
                  (checkedChange)="toggleEventAnnotation($event)"
                  [disabled]="disableEventAnnotation()"
                />
                <span class="events-toggle">{{ t("chart-renderer.events") }}</span>
              </div>

              <app-activity-event-filters
                class="flex-1"
                [displayEventAnnotation]="displayEventAnnotation()"
                [disableEventAnnotation]="disableEventAnnotation()"
                [allEventAnnotationTypes]="allEventAnnotationTypes"
              />
            </div>
          }
          <div class="flex items-center justify-end">
            <IDateAggreationSwitchButton
              [selected]="dateAggregation()"
              (dateAggSelected)="selectAggregation($event)"
              [displayTitle]="false"
            ></IDateAggreationSwitchButton>
          </div>
        </div>
      </div>
      <IChart class="min-h-0 flex-1" [config]="chartConfig()" [data]="_chartData()?.data ?? []" />
    }
  </div>`,
  imports: [
    CommonModule,
    MetricSelectorComponent,
    SpinnerComponent,
    ActivityEventFiltersComponent,
    TranslocoDirective,
    DateRangeSelectorComponent,
    IDateAggreationSwitchComponent,
    IToggleComponent,
    IChartComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChartRendererComponent<T extends CurrencyStat> implements OnInit {
  private readonly activityEventAnnotationsService = inject(ActivityEventAnnotationsService);
  private readonly userSelectionService = inject(UserSelectionService);
  private readonly destroyRef = inject(DestroyRef);

  readonly locale = input.required<string>();
  readonly currency = input.required<Currency>();
  readonly annotations$ = input<Observable<DataSetEventAnnotation[]>>();
  readonly dataSetConfiguration = input.required<DataSetConfiguration<T>>();
  readonly chartData = input.required<Observable<ChartData<T>>>();
  readonly selectedMetrics = input.required<Metric<T>[]>();
  readonly metrics = input.required<Metric<T>[]>();
  readonly displayManagedUnmanagedMetrics = input<boolean>(false);
  readonly localStorageKey = input.required<MetricsSelectorKey>();
  readonly strategyTargetHistory = input<boolean>(false);
  readonly withRangeSelector = input<boolean>(true);
  readonly splitData = input<{ splitData1: T; splitData2: T; type: AdStatsType } | undefined>(undefined);

  readonly dateRange = toSignal(this.userSelectionService.selectedDateRange$, { requireSync: true });

  readonly totalData = computed(() =>
    this.chartData().pipe(
      map((data) => data.totalData),
      startWith({} as T),
    ),
  );
  readonly totalPreviousData = computed(() =>
    this.chartData().pipe(
      map((data) => data.totalPreviousData),
      startWith({} as T),
    ),
  );
  readonly loading = signal(true);
  readonly dateAggregation = signal(DateAggregation.daily);
  readonly _selectedMetrics = linkedSignal<Metric<T>[], Metric<T>[]>({
    source: this.selectedMetrics,
    computation: (_, previous) => {
      if (!this._chartData()) {
        return previous?.value ?? [];
      }
      return this.extractMetrics(previous?.value ?? [], this._chartData()!);
    },
    equal: () => false,
  });
  readonly displayEventAnnotation = signal(false);
  readonly disableEventAnnotation = computed(() => this.dateAggregation() !== DateAggregation.daily);
  readonly allEventAnnotationTypes: Option<ActivityEventType>[] =
    this.activityEventAnnotationsService.allActivityEventTypesOptions;

  readonly _chartData = signal<ChartDataTyped<T> | undefined>(undefined);
  private readonly periodComparison = toSignal(this.userSelectionService.periodComparison$, { requireSync: true });
  private readonly previousData = signal<T[] | undefined>(undefined);
  private readonly annotations = signal<DataSetEventAnnotation[] | undefined>(undefined);

  readonly chartConfig = computed<ChartConfig<T, "line">>(() => {
    const showEventsAnnotation = this.displayEventAnnotation() && this.dateAggregation() === DateAggregation.daily;

    return {
      type: "line",
      aggFunctions: [...this.dataSetConfiguration().aggregationFunction],
      metrics: this._selectedMetrics(),
      dateAggregation: this.dateAggregation(),
      dateRange: this.dateRange(),
      previousPeriodData: this.previousData(),
      previousPeriod: this.periodComparison()?.period,
      metricsOnSameScale: this.dataSetConfiguration().metricsOnSameScale ?? [],
      eventAnnotations: showEventsAnnotation ? this.annotations() : undefined,
      additionalChartOptions: { maintainAspectRatio: false },
    };
  });

  ngOnInit(): void {
    this.chartData()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        this.previousData.set(data.previousData);
      });
    this.annotations$()
      ?.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((annotations) => {
        this.annotations.set(annotations);
      });
    this.chartData()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((_) => {
          this.loading.set(true);
        }),
      )
      .subscribe((chartData) => {
        if (this.strategyTargetHistory()) {
          let acosTarget = false;
          let tacosTarget = false;
          let monthlyBudget = false;
          let dailyBudget = false;
          let minDailySpend = false;

          for (const data of chartData.data) {
            if (!isStrategyHistoryConfig(data)) {
              continue;
            }
            if (data.acosTarget) {
              acosTarget = true;
            }
            if (data.tacosTarget) {
              tacosTarget = true;
            }
            if (data.monthlyBudget) {
              monthlyBudget = true;
            }
            if (data.dailyBudget) {
              dailyBudget = true;
            }
            if (data.minDailySpend) {
              minDailySpend = true;
            }
          }
          this._chartData.set({
            ...chartData,
            acosTarget,
            tacosTarget,
            monthlyBudget,
            dailyBudget,
            minDailySpend,
          });
        } else {
          this._chartData.set(chartData);
        }
        this.loading.set(false);
      });
  }

  private extractMetrics(
    selectedMetrics: Metric<T>[],
    chartData: ChartData<T> & {
      acosTarget?: boolean;
      tacosTarget?: boolean;
      monthlyBudget?: boolean;
      dailyBudget?: boolean;
      minDailySpend?: boolean;
    },
  ) {
    // _selectedMetrics feeds its previous value back in: drop the overlays added last time or they pile up
    const metrics = selectedMetrics.filter((m) => !STRATEGY_CONFIG_METRICS.some((configMetric) => configMetric === m));
    if (this.strategyTargetHistory()) {
      if (metrics.includes(ACOS) && chartData.acosTarget == true) {
        metrics.push(TARGET_ACOS);
      }
      if (metrics.includes(TACOS) && chartData.tacosTarget == true) {
        metrics.push(TARGET_TACOS);
      }
      if (metrics.includes(COST)) {
        if (chartData.minDailySpend == true) {
          metrics.push(MIN_DAILY_SPEND);
        }
        if (chartData.dailyBudget == true) {
          metrics.push(DAILY_BUDGET);
        }
        if (chartData.monthlyBudget == true) {
          metrics.push(MONTHLY_BUDGET);
        }
      }
    }
    return metrics;
  }

  selectMetrics(metrics: Metric<T>[]): void {
    const chartData = this._chartData();
    this._selectedMetrics.set(chartData ? this.extractMetrics(metrics, chartData) : metrics);
  }

  selectAggregation(dateAggregation: DateAggregation): void {
    this.dateAggregation.set(dateAggregation);
  }

  toggleEventAnnotation(change: boolean): void {
    this.displayEventAnnotation.set(change);
  }
}

type StrategyConfigHistory = {
  acosTarget?: number;
  tacosTarget?: number;
  monthlyBudget?: number;
  dailyBudget?: number;
  minDailySpend?: number;
};

function isStrategyHistoryConfig(arg: unknown): arg is StrategyConfigHistory {
  return (
    (arg as StrategyConfigHistory).acosTarget !== undefined ||
    (arg as StrategyConfigHistory).tacosTarget !== undefined ||
    (arg as StrategyConfigHistory).monthlyBudget !== undefined ||
    (arg as StrategyConfigHistory).dailyBudget !== undefined ||
    (arg as StrategyConfigHistory).minDailySpend !== undefined
  );
}
